import React, { useMemo, useState, Fragment } from 'react';
import {
  Box, TextField, IconButton, Typography, Button, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

// CollectionCarousel
// ---------------------------------------------------------------
// Shared list pattern for the app:
//   [ search field ] [ ◀ page X / Y ▶ ]            [ Show all (N) ]
//   ┌────────────────── current page slice ────────────────┐
//   │ renderItem(item) for each item in the active page    │
//   └──────────────────────────────────────────────────────┘
// "Show all" opens a Dialog with the same search box and the full filtered
// list — no truncation in the popup, scroll if it's long.
//
// `layout` controls the page-slice container:
//   - 'wrap'  → flex wrap (chips, pills)
//   - 'stack' → vertical Stack (cards)
//   - 'grid2' → two-column grid (compact cards)
export default function CollectionCarousel({
  items,
  renderItem,
  getItemKey = (item, i) => item?.id ?? item?.method_id ?? i,
  searchFn,                         // (item, lowercasedQuery) => boolean
  pageSize = 6,
  searchPlaceholder = 'Search…',
  showAllTitle = 'All items',
  emptyText = 'No items match',
  layout = 'wrap',                  // 'wrap' | 'stack' | 'grid2'
  itemGap = 1,
}) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    if (!query.trim() || !searchFn) return items;
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      try { return searchFn(it, q); } catch { return false; }
    });
  }, [items, query, searchFn]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const slice = filtered.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  const hasPager = filtered.length > pageSize;

  const gridCols = (n) => ({
    display: 'grid',
    gridTemplateColumns: {
      xs: 'minmax(0, 1fr)',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: `repeat(${n}, minmax(0, 1fr))`,
    },
  });

  const containerSx =
    layout === 'stack'
      ? { display: 'flex', flexDirection: 'column', gap: itemGap }
      : layout === 'grid3'
        ? { ...gridCols(3), gap: itemGap }
        : layout === 'grid2'
          ? { ...gridCols(2), gap: itemGap }
          : { display: 'flex', flexWrap: 'wrap', gap: itemGap };

  const fullContainerSx =
    layout === 'stack'
      ? { display: 'flex', flexDirection: 'column', gap: 1 }
      : layout === 'grid3'
        ? { ...gridCols(3), gap: 1 }
        : layout === 'grid2'
          ? { ...gridCols(2), gap: 1 }
          : { display: 'flex', flexWrap: 'wrap', gap: 1 };

  return (
    <Box>
      {/* Toolbar: compact search + pager + show-all */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 1,
          flexWrap: 'wrap',
        }}
      >
        {searchFn && (
          <TextField
            size="small"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            placeholder={searchPlaceholder}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.8rem', height: 32 },
            }}
            sx={{ width: { xs: '100%', sm: 200 } }}
          />
        )}

        {hasPager && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: 'center' }}>
              {currentPage + 1} / {totalPages}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {items.length > pageSize && (
          <Button
            size="small"
            onClick={() => setShowAll(true)}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Show all ({items.length})
          </Button>
        )}
      </Box>

      {/* Current page slice */}
      {filtered.length === 0 ? (
        <Typography variant="caption" color="text.secondary">{emptyText}</Typography>
      ) : (
        <Box sx={containerSx}>
          {slice.map((item, i) => (
            <Fragment key={getItemKey(item, currentPage * pageSize + i)}>
              {renderItem(item)}
            </Fragment>
          ))}
        </Box>
      )}

      {/* Show-all popup */}
      <Dialog
        open={showAll}
        onClose={() => setShowAll(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          {showAllTitle}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            ({filtered.length} of {items.length})
          </Typography>
          <IconButton
            onClick={() => setShowAll(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {searchFn && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
          )}
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
          ) : (
            <Box sx={fullContainerSx}>
              {filtered.map((item, i) => (
                <Fragment key={getItemKey(item, i)}>{renderItem(item)}</Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAll(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
